/**
 * useTaskAiCalls — real-time subscription to per-phase AI-call snapshots
 * captured by `runPostPipeline` (Section 7.6.4).
 *
 * Source: `projects/{projectId}/backend_tasks/{taskId}/ai_calls/*`. Each
 * doc represents one (unit, AI-call phase) combination — for cope chain
 * that's usually `post_generation_planning`, `master_generation`, and
 * `derivative_generation` per cell run; for direct mode it's
 * `post_generation_planning` + `direct_generation`.
 *
 * Doc shape (informational — actual fields decoded loose so the hook
 * doesn't pin to schema-models bumps):
 *   {
 *     phase: string,                     // UI-facing AI-call phase label
 *     pipelinePhase?: string,             // internal pipeline key when different
 *     handler: 'MasterPostHandler' | 'DerivativePostHandler' | ...,
 *     status: 'completed' | 'error',
 *     inputSummary: { topicId, platform, model, ... },
 *     taskPhaseKey?: string,            // parent task phase this call belongs to
 *     systemPrompt?: string,            // exact system prompt sent to model
 *     userMessage?: string,             // exact user message after input injection
 *     fullPromptMarkdown?: string,
 *     rawResponse?: string,
 *     parsed?: string,                  // JSON-stringified provider parsed content
 *     result?: string,                  // JSON-stringified handler-normalised result
 *     parseFailureReason?: string,
 *     capturedModel?: string,
 *     capturedUsage?: { inputTokens, outputTokens, totalTokens },
 *     patchUsage?: { ... },
 *     error?: string,
 *     durationMs: number,
 *     startedAt, completedAt: Timestamp,
 *   }
 */

import { useEffect, useState } from 'react';
import {
  collection,
  getDocs,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';

export interface AiCallDoc {
  id: string;
  phase: string;
  pipelinePhase?: string;
  taskPhaseKey?: string;
  handler?: string;
  status?: 'running' | 'completed' | 'error';
  inputSummary?: Record<string, unknown>;
  systemPrompt?: string;
  userMessage?: string;
  fullPromptMarkdown?: string;
  rawResponse?: string;
  parsed?: string;
  result?: string;
  chunkedFields?: Record<string, { chunks: number; chars: number; version: string }>;
  parseFailureReason?: string;
  capturedModel?: string;
  capturedUsage?: { inputTokens?: number; outputTokens?: number };
  patchUsage?: { inputTokens?: number; outputTokens?: number };
  error?: string;
  durationMs?: number;
  startedAt?: { seconds: number; nanoseconds: number } | Date;
  completedAt?: { seconds: number; nanoseconds: number } | Date;
}

interface AiCallPayloadChunk {
  field: keyof Pick<
    AiCallDoc,
    'systemPrompt' | 'userMessage' | 'fullPromptMarkdown' | 'rawResponse' | 'parsed' | 'result'
  >;
  index: number;
  text: string;
  version: string;
}

async function hydrateChunkedFields(
  db: ReturnType<typeof CoreFirebaseService.getInstance>['firestore'],
  projectId: string,
  taskId: string,
  call: AiCallDoc,
): Promise<AiCallDoc> {
  if (!call.chunkedFields || Object.keys(call.chunkedFields).length === 0) return call;
  const chunksRef = collection(
    db,
    'projects',
    projectId,
    'backend_tasks',
    taskId,
    'ai_calls',
    call.id,
    'payload_chunks',
  );
  const snap = await getDocs(chunksRef);
  const chunks = snap.docs.map((d) => d.data() as AiCallPayloadChunk);
  const hydrated: AiCallDoc = { ...call };

  for (const [field, meta] of Object.entries(call.chunkedFields)) {
    const fieldChunks = chunks
      .filter((chunk) => chunk.field === field && chunk.version === meta.version)
      .sort((a, b) => a.index - b.index);
    if (fieldChunks.length === meta.chunks) {
      (hydrated as any)[field] = fieldChunks.map((chunk) => chunk.text).join('');
    }
  }

  return hydrated;
}

export interface UseTaskAiCallsReturn {
  calls: AiCallDoc[];
  loading: boolean;
  error: Error | null;
}

function emitTaskAiCallsUpdate(
  projectId: string,
  taskId: string,
  calls: AiCallDoc[],
): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('segmently:task-ai-calls:update', {
    detail: {
      projectId,
      taskId,
      callCount: calls.length,
      phases: calls.map((call) => call.pipelinePhase ?? call.taskPhaseKey ?? call.phase),
      calls: calls.map((call) => ({
        id: call.id,
        phase: call.phase,
        pipelinePhase: call.pipelinePhase,
        taskPhaseKey: call.taskPhaseKey,
        status: call.status,
        hasSystemPrompt: Boolean(call.systemPrompt),
        hasUserMessage: Boolean(call.userMessage),
        hasRawResponse: Boolean(call.rawResponse),
        hasResult: Boolean(call.result),
      })),
    },
  }));
}

export function useTaskAiCalls(
  projectId: string | null | undefined,
  taskId: string | null | undefined,
): UseTaskAiCallsReturn {
  const [calls, setCalls] = useState<AiCallDoc[]>([]);
  const [loading, setLoading] = useState<boolean>(Boolean(projectId && taskId));
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId || !taskId) {
      setCalls([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const db = CoreFirebaseService.getInstance().firestore;
    let cancelled = false;
    const ref = collection(
      db, 'projects', projectId, 'backend_tasks', taskId, 'ai_calls',
    );
    const unsub = onSnapshot(
      query(ref, orderBy('startedAt', 'asc')),
      (snap) => {
        const baseCalls = snap.docs.map((d) => ({
          ...(d.data() as Omit<AiCallDoc, 'id'>),
          id: d.id,
        }));
        Promise.all(baseCalls.map((call) => hydrateChunkedFields(db, projectId, taskId, call)))
          .then((hydratedCalls) => {
            if (cancelled) return;
            setCalls(hydratedCalls);
            emitTaskAiCallsUpdate(projectId, taskId, hydratedCalls);
            setLoading(false);
          })
          .catch((err) => {
            if (cancelled) return;
            setCalls(baseCalls);
            emitTaskAiCallsUpdate(projectId, taskId, baseCalls);
            setError(err as Error);
            setLoading(false);
          });
      },
      (err) => {
        // Missing-collection / permission errors fall through quietly —
        // task may simply not have any AI calls yet.
        setError(err as Error);
        setLoading(false);
      },
    );
    return () => {
      cancelled = true;
      unsub();
    };
  }, [projectId, taskId]);

  return { calls, loading, error };
}
